// @ts-check
// Ventana emergente que pide UN dato por teclado a mitad de una ejecución,
// compartida por las dos consolas de los editores de texto
// (`pseudocode-editor/run-console.js`, `python-editor/execution-console.js`).
//
// ## Qué se comparte y qué NO
//
// Las dos consolas divergen mucho, y con razón: una ejecuta un intérprete
// SÍNCRONO en el hilo principal y la otra `await`ea un Worker con Pyodide. Eso
// NO se toca: ni el bucle de ejecución, ni el pintado de la salida, ni el
// manejo de errores son iguales ni deberían serlo.
//
// Lo que sí era el mismo código copiado es el PROTOCOLO de entrada
// interactiva: el tope defensivo de datos, el mensaje de cancelación byte a
// byte, y el cuarteto `finalizarEntrada`/`pedirDato`/`enviarEntrada`/
// `cancelarEntrada` alrededor de una promesa que se resuelve con el dato o con
// `null`. Eso es lo que vive aquí.
//
// Este módulo ENVUELVE `components/modal-dialog.js`, que ya era compartido: no
// reimplementa el diálogo (foco atrapado, `Escape`, clic fuera, restauración
// del foco al cerrar), solo le añade el campo, el contexto y el protocolo.
//
// ## El contrato de la promesa, que es lo delicado
//
//   - `pedirDato(contexto)` abre el modal y devuelve una promesa.
//   - Se resuelve con la CADENA introducida al pulsar "Enviar"/Enter.
//   - Se resuelve con `null` al cancelar por CUALQUIER vía: botón "Cancelar",
//     `Escape`, clic fuera, botón de cerrar del diálogo, o `destruir()`.
//   - Nunca se rechaza, y nunca se queda colgada: si el consumidor destruye la
//     consola con una petición en vuelo, `destruir()` resuelve con `null` para
//     que el bucle de ejecución despierte y pueda salir.
//
// El caso que hace falta tener presente al leer `enviarEntrada`: llamar a
// `modal.cerrar()` directamente NO dispara `onCerrar` (así lo define
// `modal-dialog.js`), por eso el valor se resuelve a mano justo después.

import { crearModalDialogo } from './modal-dialog.js';
import { createSafeElement, setSafeText } from '../security/safe-dom.js';

/** Lo que se anuncia cuando el alumno cancela en vez de introducir el dato. */
export const MENSAJE_ENTRADA_CANCELADA =
  'Ejecución cancelada: no se introdujo el dato solicitado.';

/**
 * Tope defensivo de datos por ejecución. Con el enfoque "replay" (se reejecuta
 * el programa entero con los datos ya introducidos), un bucle infinito de
 * `Leer`/`input()` pediría datos sin fin: esto lo corta.
 */
export const LIMITE_ENTRADAS = 100;

/**
 * Lo que se pide cuando quien ejecuta no sabe decir nada más concreto. Las
 * dos consolas de texto están en este caso: ni el intérprete de pseudocódigo
 * ni Pyodide dicen QUÉ variable se está leyendo, solo que hace falta un dato.
 * El diagrama sí lo sabe (el motor devuelve `variable_pendiente`), y por eso
 * `pedirDato` acepta una etiqueta propia.
 */
export const ETIQUETA_POR_DEFECTO = 'Tu programa espera un dato. Escríbelo y pulsa «Enviar»:';

/**
 * @typedef {object} ModalPeticionDato
 * @property {HTMLElement} elemento Raíz del diálogo, para añadirla al DOM.
 * @property {(contexto: string, etiqueta?: string) => Promise<string | null>} pedirDato
 * @property {() => void} destruir
 */

/**
 * @param {{
 *   idCampo: string,
 *   prefijoClase: string,
 *   claseCampo: string,
 *   obtenerOrigenFoco: () => HTMLElement,
 * }} opciones
 *   `prefijoClase` nombra el modal/contexto/etiqueta y los botones
 *   (`<prefijo>-input-modal`, `<prefijo>-run`, ...). `claseCampo` va aparte
 *   porque las dos consolas NO la derivan igual (`pseudo-run-console-input`
 *   frente a `execution-console-input-field`) y sus hojas de estilo ya están
 *   escritas contra esos nombres exactos. `obtenerOrigenFoco` devuelve el
 *   elemento al que el diálogo devuelve el foco al cerrarse (el botón
 *   "Ejecutar"); es una función porque se consulta al abrir, no al construir.
 * @returns {ModalPeticionDato}
 */
export function crearModalPeticionDato({
  idCampo,
  prefijoClase,
  claseCampo,
  obtenerOrigenFoco,
}) {
  const modal = crearModalDialogo({
    titulo: 'Se necesita un dato',
    // Escape / clic fuera / botón de cerrar => cancelar la petición de dato.
    onCerrar: () => finalizarEntrada(null),
  });
  modal.elemento.classList.add(`${prefijoClase}-input-modal`);

  // Contexto: lo que el programa ya ha escrito hasta pedir el dato. Es texto
  // NO confiable (lo produce el código del alumno), así que SIEMPRE por
  // `textContent`/`setSafeText`, nunca `innerHTML` (SEC-R1). Oculto si vacío.
  const contexto = createSafeElement('pre', {
    className: `${prefijoClase}-input-context`,
  });
  contexto.hidden = true;

  const etiquetaCampo = createSafeElement('label', {
    className: `${prefijoClase}-input-label`,
    text: ETIQUETA_POR_DEFECTO,
    attrs: { for: idCampo },
  });

  const campo = document.createElement('input');
  campo.type = 'text';
  campo.id = idCampo;
  campo.className = claseCampo;
  campo.autocomplete = 'off';
  campo.spellcheck = false;

  modal.cuerpo.append(contexto, etiquetaCampo, campo);

  const botonEnviar = document.createElement('button');
  botonEnviar.type = 'button';
  botonEnviar.className = `${prefijoClase}-run`;
  botonEnviar.textContent = 'Enviar';

  const botonCancelar = document.createElement('button');
  botonCancelar.type = 'button';
  botonCancelar.className = `${prefijoClase}-run ${prefijoClase}-run-secondary`;
  botonCancelar.textContent = 'Cancelar';

  modal.acciones.append(botonCancelar, botonEnviar);

  /** @type {((valor: string | null) => void) | null} */
  let resolverEntrada = null;

  /** @param {string | null} valor */
  function finalizarEntrada(valor) {
    if (!resolverEntrada) return;
    const resolver = resolverEntrada;
    resolverEntrada = null;
    resolver(valor);
  }

  /**
   * Abre el modal y resuelve con el dato introducido, o `null` si se cancela.
   * @param {string} textoContexto Salida escrita hasta pedir el dato.
   * @param {string} [etiqueta] Qué se está pidiendo exactamente, cuando quien
   *   ejecuta lo sabe (p. ej. `Escribe el valor de «numero»…`). Sin ella se
   *   usa `ETIQUETA_POR_DEFECTO`. Es texto de la aplicación, pero puede
   *   llevar dentro un nombre de variable escrito por el alumno, así que se
   *   fija con `setSafeText` como todo lo demás (SEC-R1).
   * @returns {Promise<string | null>}
   */
  function pedirDato(textoContexto, etiqueta) {
    return new Promise((resolve) => {
      resolverEntrada = resolve;
      setSafeText(etiquetaCampo, etiqueta || ETIQUETA_POR_DEFECTO);
      if (textoContexto) {
        setSafeText(contexto, textoContexto);
        contexto.hidden = false;
      } else {
        contexto.textContent = '';
        contexto.hidden = true;
      }
      campo.value = '';
      modal.abrir(obtenerOrigenFoco());
      campo.focus();
    });
  }

  function enviarEntrada() {
    const valor = campo.value;
    modal.cerrar(); // cerrar() directo NO dispara onCerrar
    finalizarEntrada(valor);
  }

  function cancelarEntrada() {
    modal.cerrar();
    finalizarEntrada(null);
  }

  /** @param {KeyboardEvent} evento */
  function alTeclearCampo(evento) {
    if (evento.key === 'Enter') {
      evento.preventDefault();
      enviarEntrada();
    }
  }

  botonEnviar.addEventListener('click', enviarEntrada);
  botonCancelar.addEventListener('click', cancelarEntrada);
  campo.addEventListener('keydown', alTeclearCampo);

  function destruir() {
    // Resuelve una petición de dato pendiente para no dejar colgado el bucle
    // de ejecución del consumidor (que comprobará su propio `destruido`).
    finalizarEntrada(null);
    botonEnviar.removeEventListener('click', enviarEntrada);
    botonCancelar.removeEventListener('click', cancelarEntrada);
    campo.removeEventListener('keydown', alTeclearCampo);
    modal.destruir();
  }

  return { elemento: modal.elemento, pedirDato, destruir };
}
